// Keep cloud diagnostics separate from the test result. A failed upload must
// neither abort the smoke tests nor turn their failures into a green result.
function text(value, max = 1800) {
  if (typeof value !== 'string') return ''
  return value
    .replace(/https?:\/\/[^\s<>"']+/gi, '[URL removed]')
    .replace(/\bBearer\s+\S+/gi, '[credential removed]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[JWT removed]')
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email removed]')
    .replace(
      /\b(?:access_token|refresh_token|id_token|apiKey|password|authorization)\s*[=:]\s*[^\s,;]+/gi,
      '[credential removed]'
    )
    .slice(0, max)
}
function counts(results) {
  return Object.fromEntries(
    ['pass', 'fail', 'skip', 'blocked'].map((status) => [
      status,
      results.filter((s) => s.status === status).length
    ])
  )
}
function cleanup(value) {
  return value
    ? {
        deleted: value.deleted || 0,
        storageDeleted: value.storageDeleted || 0,
        errors: (value.errors || []).slice(0, 20).map((e) => text(e, 1000))
      }
    : null
}

export function cloudSnapshot(report) {
  const results = report.results || []
  return {
    runId: report.runId,
    metadata: {
      mode: report.mode || 'single',
      startedAt: report.startedAt || null,
      finishedAt: report.finishedAt || null,
      startingRole: report.user?.startingRole || report.user?.role || null,
      restoredRole: report.user?.restoredRole || null,
      restoreError: text(report.restoreError, 1000),
      runError: text(report.runError, 1000),
      summary: counts(results),
      cleanup: cleanup(report.cleanup),
      roles: (report.roles || []).map((r) => ({
        role: r.role,
        status: r.status,
        rotateError: text(r.rotateError, 1000),
        cleanup: cleanup(r.cleanup),
        summary: counts(results.filter((s) => s.role === r.role))
      }))
    },
    steps: results.map((s, index) => ({
      index,
      scenario: text(s.scenario, 80),
      step: text(s.step, 600),
      role: s.role || report.user?.role || null,
      status: s.status,
      durationMs: s.durationMs || 0,
      detail: text(s.detail),
      error: text(s.error),
      errorCode: text(s.errorCode, 100)
    }))
  }
}

export function createCloudReportQueue({ send, onStatus = () => {}, intervalMs = 5000 }) {
  let latest,
    timer,
    active,
    pending,
    revision = 0,
    closed = false,
    finalState = null
  const saved = new Map()
  const status = (value) => onStatus({ runId: latest?.runId, ...value })

  async function acknowledge() {
    const response = await send(pending)
    for (const step of pending.steps) saved.set(step.index, JSON.stringify(step))
    revision = pending.revision
    const state = pending.state
    pending = null
    if (state !== 'running') finalState = state
    status({ status: 'saved', path: response.path, state, savedSteps: saved.size })
  }

  async function upload(state) {
    try {
      status({ status: 'saving' })
      // Retry the exact same payload/revision after an ambiguous network error.
      if (pending) await acknowledge()
      if (finalState) return
      const snapshot = cloudSnapshot(latest)
      const changed = snapshot.steps.filter((s) => saved.get(s.index) !== JSON.stringify(s))
      const chunks = []
      for (let i = 0; i < changed.length; i += 100) chunks.push(changed.slice(i, i + 100))
      if (!chunks.length) chunks.push([])
      for (let i = 0; i < chunks.length; i++) {
        pending = {
          runId: snapshot.runId,
          revision: revision + 1,
          state: i === chunks.length - 1 ? state : 'running',
          stepCount: snapshot.steps.length,
          metadata: snapshot.metadata,
          steps: chunks[i]
        }
        await acknowledge()
      }
    } catch (error) {
      status({ status: 'error', error: text(error?.message || String(error), 500) })
    }
  }

  function flush(state = 'running') {
    if (!latest) return Promise.resolve()
    if (active) return active
    clearTimeout(timer)
    timer = null
    active = upload(state).finally(() => {
      active = null
    })
    return active
  }

  return {
    publish(report) {
      if (closed) return
      const first = !latest
      latest = report
      if (first) void flush()
      else if (!timer)
        timer = setTimeout(() => {
          timer = null
          void flush()
        }, intervalMs)
    },
    async finish(report, state = 'completed') {
      closed = true
      if (report) latest = report
      clearTimeout(timer)
      if (active) await active
      await flush(state)
    },
    async retry() {
      if (active) await active
      await flush(closed ? (latest?.runError ? 'aborted' : 'completed') : 'running')
    }
  }
}
